// A sensor, opened, on the desktop shell — the counterpart to the mobile
// screen of the same name, and driven by the same core parser so the two can
// never disagree about what green means.
//
// It replaces UnsupportedScreen for these devices, which built toggles from the
// device's outputs and, finding none, said "No controllable outputs found." to
// the owner of a working door sensor.

import React from 'react';
import { Box, Typography } from '@mui/material';
import DeviceShell from '../../components/DeviceShell';
import RfDevicesPanel from '../../../shared/RfDevicesPanel';
import RfLastHeard from '../../../shared/RfLastHeard';
import { useDeviceStatus } from '../../../../core/mqtt/useDeviceStatus';
import {
  sensorStateOf, SENSOR_ALERT, SENSOR_NORMAL,
} from '../../../../core/devices/sensorState';

const COLOURS = {
  [SENSOR_NORMAL]: '#2E7D32',
  [SENSOR_ALERT]: '#C62828',
};
const NEUTRAL = '#9E9E9E';

// ⚠️ THE RF RECEIVER IS A SENSOR WITH A SCREEN'S WORTH OF WORK BEHIND IT.
// DeviceID 47 reports nothing about itself — what matters is what it HEARS: the
// door, safe, fire and motion sensors in range, none of which has a unique
// number of its own. So its sensor screen carries the RF panel.
const RF_RECEIVER_DEVICE_ID = 47;

export default function SensorScreen({ device, initialStatus = '' }) {
  const uno = device.UniqueNumber;
  const [status, , liveAt] = useDeviceStatus(uno, initialStatus || device.Status || '');
  const { state, label } = sensorStateOf(status, device.DeviceID);
  const colour = COLOURS[state] || NEUTRAL;

  return (
    <DeviceShell title={device.DeviceName || uno}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4,
      }}
      >
        {/* A receiver says WHICH sensor and what it said, not its raw code. */}
        {Number(device?.DeviceID) === RF_RECEIVER_DEVICE_ID ? (
          <RfLastHeard
            receiverUno={uno}
            status={status}
            liveAt={liveAt}
            locationId={device?.LocationID ?? device?.LocationId ?? 0}
          />
        ) : (
        <>
        <Box sx={{
          width: 140,
          height: 140,
          borderRadius: '50%',
          bgcolor: colour,
          boxShadow: `0 0 0 12px ${colour}1F`,
        }}
        />
        <Typography variant="h5" fontWeight={700} sx={{ color: colour, textTransform: 'uppercase' }}>
          {label}
        </Typography>
        </>
        )}
        {/* `liveAt`, not `status` — the seed is the server's last-known value
            and says nothing about whether the device is there. */}
        <Typography variant="caption" color="text.secondary">
          {liveAt ? 'Live' : 'Last known state'}
        </Typography>
      </Box>
      {Number(device?.DeviceID) === RF_RECEIVER_DEVICE_ID && (
        <Box sx={{ px: 1, pb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>RF devices heard by this receiver</Typography>
          <RfDevicesPanel
            receiverUno={uno}
            locationId={device?.LocationID ?? device?.LocationId ?? 0}
          />
        </Box>
      )}
    </DeviceShell>
  );
}
